/**
 * Article rédigé le 2026-09-07 à partir des pages publiques de kohai.fr et du registre national
 * des associations (annuaire des entreprises). Toutes les URL ont été ouvertes et vérifiées.
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "kohai-association-deep-tech-besancon",
  "title": "KOHAI, l'association bisontine qui veut faire de la technique un bien commun",
  "excerpt": "Créée en 2025 à Besançon, KOHAI accompagne les porteurs de projets deep tech et transmet les savoirs de l'IA. Une association qui fait de la technique un bien commun.",
  "seoTitle": "KOHAI, l'association deep tech bisontine",
  "seoDescription": "Déclarée en juin 2025, KOHAI accompagne les porteurs de projets deep tech et transmet les savoirs en IA. Portrait de l'association et de ses deux cofondateurs.",
  "category": "startups",
  "tags": [
    "KOHAI",
    "Deeptech",
    "Association",
    "Transmission",
    "Mehdi Mougin"
  ],
  "author": "jimmy",
  "publishedAt": "2026-09-05",
  "updatedAt": "2026-09-05",
  "featured": false,
  "cover": {
    "image": "/img/blog/kohai-logo-cover.png",
    "plain": true,
    "credit": "Logo : KOHAI",
    "alt": "Logo de l’association KOHAI : le mot « KOHAI » en lettres de bois, avec un palmier et une flamme"
  },
  "content": [
    {
      "type": "lead",
      "text": "Entre le laboratoire qui publie et la start-up qui lève, il existe un angle mort : celui du porteur de projet qui a une bonne idée technique et personne à qui poser les vraies questions. C'est là que se place **KOHAI**, association bisontine fondée en 2025 par [Mehdi Mougin](/blog/mehdi-mougin-signon-french-tech-tremplin-besancon) et Jimmy Inthalangsy. Sa conviction tient en trois mots : « La technique. Un bien commun. »"
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Quoi",
          "value": "Association à but non lucratif d'aide technique aux projets deep tech"
        },
        {
          "label": "Créée",
          "value": "Déclarée le 13 juin 2025 à Besançon"
        },
        {
          "label": "Cofondateurs",
          "value": "Mehdi Mougin et Jimmy Inthalangsy"
        },
        {
          "label": "Domaines",
          "value": "Intelligence artificielle, logiciel, web, données, objets connectés"
        },
        {
          "label": "Deux activités",
          "value": "Accompagnement de projets et transmission de savoirs"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une association, pas un prestataire"
    },
    {
      "type": "p",
      "text": "KOHAI se présente comme une « association à but non lucratif » dédiée à « l'aide technique aux projets deep tech et transmission des savoirs en intelligence artificielle ».[^1] Le registre national des associations la confirme : déclarée le 13 juin 2025 à Besançon, elle est enregistrée sous le code d'activité des organisations fonctionnant par adhésion volontaire.[^2]"
    },
    {
      "type": "p",
      "text": "Deux cofondateurs portent l'association. **Mehdi Mougin**, ingénieur en intelligence artificielle, fondateur de SIGN ON — lauréat French Tech Tremplin Incubation 2026 et coup de cœur du public French Tech Tremplin Prépa 2025 — apporte l'expérience de celui qui a mené un projet deep tech du prototype à la reconnaissance nationale. **Jimmy Inthalangsy**, développeur IA, y apporte une culture de l'apprentissage et de la transmission.[^3] Deux profils complémentaires, qui disent bien la double vocation de la structure : construire, et expliquer."
    },
    {
      "type": "p",
      "text": "Le choix du nom n'est pas anodin. En japonais, *kōhai* désigne le cadet, celui qui apprend, par opposition au *senpai* qui transmet. Une association qui se nomme d'après l'élève plutôt que d'après le maître affiche d'emblée de quel côté elle se range."
    },
    {
      "type": "h2",
      "text": "Accompagner : cinq étapes, du besoin à la preuve"
    },
    {
      "type": "p",
      "text": "Le premier volet vise les porteurs de projets à un stade précoce. KOHAI dit intervenir « lorsque les questions sont encore nombreuses et que les choix techniques peuvent orienter toute la suite ».[^4] La formulation vise juste : dans un projet technologique, les décisions d'architecture prises les premières semaines coûtent des mois à défaire."
    },
    {
      "type": "p",
      "text": "La méthode est publiée en cinq temps :"
    },
    {
      "type": "ol",
      "items": [
        "**Écouter** — saisir le projet et sa maturité ;",
        "**Cadrer** — formuler le problème et exposer les incertitudes ;",
        "**Explorer** — comparer les approches possibles ;",
        "**Expérimenter** — construire des preuves de concept ;",
        "**Transmettre** — documenter et partager les compétences."
      ]
    },
    {
      "type": "p",
      "text": "La dernière étape mérite qu'on s'y arrête. Un prestataire classique livre un résultat et repart avec le savoir-faire ; ici l'accompagnement se termine explicitement par un transfert de compétences vers l'équipe. C'est cohérent avec la ligne affichée par l'association : une aide doit être « utile au projet » et « compréhensible par l'équipe ».[^3]"
    },
    {
      "type": "h2",
      "text": "Transmettre : viser l'autonomie et le discernement"
    },
    {
      "type": "p",
      "text": "Le second volet est pédagogique, articulé lui aussi en cinq temps — explications progressives, mise en pratique, expérimentation guidée, méthodes et ressources, échanges entre profils — sur trois domaines : intelligence artificielle, développement logiciel et objets connectés.[^5]"
    },
    {
      "type": "p",
      "text": "L'objectif énoncé est intéressant : « La transmission vise l'autonomie et le discernement. » Le second terme est rare dans la communication technologique. À l'heure où l'on apprend surtout à *utiliser* des outils d'IA, savoir juger de ce qu'ils valent, de ce qu'ils coûtent et de ce qu'ils ne savent pas faire est probablement la compétence la moins enseignée."
    },
    {
      "type": "p",
      "text": "Trois principes d'action structurent l'ensemble : partir du besoin, apprendre par le réel, partager les savoirs.[^3] Et une devise, presque une profession de foi : « La passion comme point de départ. Le partage comme suite. »"
    },
    {
      "type": "h2",
      "text": "Un maillon qui manquait dans l'écosystème local"
    },
    {
      "type": "p",
      "text": "Besançon ne manque pas de structures d'accompagnement : [TEMIS](/blog/temis-technopole-startups-deeptech-besancon) et sa pépinière, l'incubateur régional, [la French Tech](/blog/french-tech-bfc-capitale-2026-2028-besancon), les dispositifs de la métropole. Mais toutes interviennent une fois le projet formé — quand il y a une équipe, un modèle économique, parfois déjà une société."
    },
    {
      "type": "p",
      "text": "KOHAI se place en amont, sur un terrain moins couvert : celui du porteur d'idée qui doit décider s'il entraîne un modèle ou s'il en réutilise un, s'il embarque le calcul ou s'il l'externalise, si son jeu de données suffit. Des questions qui ne relèvent ni du business plan ni de la recherche académique, et pour lesquelles il n'existe pas vraiment de guichet."
    },
    {
      "type": "p",
      "text": "Le format associatif n'est pas un pis-aller, c'est un choix : à but non lucratif, KOHAI n'a rien à vendre. Elle peut donc dire à un porteur de projet que son idée demande un autre chemin, ou qu'elle ne nécessite pas la technologie qu'il imaginait — une liberté que peu de prestataires ont. Dans une ville où la compétence est la vraie richesse, c'est un modèle qui tient."
    },
    {
      "type": "p",
      "text": "L'association travaille sans bruit, et c'est cohérent avec ce qu'elle est : une ressource, pas une vitrine. Elle est déjà citée par SIGN ON parmi les structures qui ont accompagné son développement — la société bisontine de traduction de la langue des signes est depuis lauréate du programme national French Tech Tremplin Incubation.[^6] Pour un porteur de projet du bassin, l'adresse est simple à retenir : **contact@kohai.fr**."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "KOHAI — accueil",
      "publisher": "KOHAI",
      "url": "https://kohai.fr/",
      "date": "2026-09-05",
      "type": "official"
    },
    {
      "id": 2,
      "title": "KOH AI — fiche du registre national des associations (SIREN 999 343 270)",
      "publisher": "Annuaire des entreprises, INSEE / RNA",
      "url": "https://annuaire-entreprises.data.gouv.fr/entreprise/999343270",
      "date": "2026-09-05",
      "type": "official"
    },
    {
      "id": 3,
      "title": "L'association",
      "publisher": "KOHAI",
      "url": "https://kohai.fr/association",
      "date": "2026-09-05",
      "type": "official"
    },
    {
      "id": 4,
      "title": "Accompagnement",
      "publisher": "KOHAI",
      "url": "https://kohai.fr/accompagnement",
      "date": "2026-09-05",
      "type": "official"
    },
    {
      "id": 5,
      "title": "Transmission",
      "publisher": "KOHAI",
      "url": "https://kohai.fr/transmission",
      "date": "2026-09-05",
      "type": "official"
    },
    {
      "id": 6,
      "title": "Traduction de la langue des signes en temps réel",
      "publisher": "SIGN ON",
      "url": "https://signon-app.fr/traduction-langue-des-signes-temps-reel",
      "date": "2026-09-05",
      "type": "official"
    }
  ],
  "jsonLd": [
    {
      "@context": "https://schema.org",
      "@type": "NGO",
      "name": "KOHAI",
      "alternateName": "KOH AI",
      "url": "https://kohai.fr/",
      "description": "Association à but non lucratif basée à Besançon : aide technique aux porteurs de projets deep tech et transmission de savoirs en intelligence artificielle, développement logiciel et objets connectés.",
      "foundingDate": "2025-06-13",
      "taxID": "999343270",
      "email": "contact@kohai.fr",
      "founder": [
        {
          "@type": "Person",
          "name": "Mehdi Mougin",
          "jobTitle": "Ingénieur en intelligence artificielle, fondateur de SIGN ON",
          "url": "https://www.ia-besancon.fr/blog/mehdi-mougin-signon-french-tech-tremplin-besancon",
          "sameAs": [
            "https://www.linkedin.com/in/mmdevia/",
            "https://github.com/MOUGINM",
            "https://huggingface.co/mehdiIA",
            "https://signon-app.fr/",
            "https://anoma.fr/"
          ]
        },
        {
          "@type": "Person",
          "name": "Jimmy Inthalangsy",
          "jobTitle": "Développeur en intelligence artificielle",
          "sameAs": [
            "https://github.com/jimmindev"
          ]
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Besançon",
        "addressRegion": "Bourgogne-Franche-Comté",
        "addressCountry": "FR"
      },
      "knowsAbout": [
        "Intelligence artificielle",
        "Développement logiciel",
        "Objets connectés",
        "Données"
      ],
      "sameAs": [
        "https://annuaire-entreprises.data.gouv.fr/entreprise/999343270"
      ],
      "slogan": "La technique. Un bien commun.",
      "areaServed": {
        "@type": "Place",
        "name": "Besançon et Bourgogne-Franche-Comté"
      }
    }
  ]
};
